import { Link } from "@mui/material";

import {
  faqs,
  howItWorks,
  AnalyticsHeader,
  customProgressBarLanguage,
} from "./helpers";
import { routes } from "@/global/routes";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";
import { DefaultCheckoutTerms, DefaultRegistrationTerms } from "../../default_terms_and_conditions";

const LogoImage =
  "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/Northwest%20Arkansas%20Naturals/arkansas-naturals-logo.webp";
const FindGamesBanner =
  "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/Northwest%20Arkansas%20Naturals/arkansas-naturals-gamepack-banner.png";
const BackgroundImage =
  "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/Northwest%20Arkansas%20Naturals/arkansas-naturals-background.png";
const StaticSeatMap =
  "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/Northwest%20Arkansas%20Naturals/Ballpark_map.jpg";

import type { TeamConfig } from "../types";

const config: TeamConfig = {
  teamSchema: "northwestarkansasnaturals_gamepacks",
  browserTitle: "Northwest Arkansas Naturals Game Packs",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Northwest Arkansas Naturals",

  packs: {
    enabled: true,
    dontOpenPackModal: true,
    disablePackSelectionLossModal: true,
    list: [
      {
        id: 1,
        amount: 10,
        offerMoreMessage: "",
        title: "Home Plate Premium 10 Game Pack",
        description:
          "Pick 10 games + save 29% while enjoying premium seating directly behind home plate!",
        scrollToTopWhenSelecting: true,
        buckets: [
          {
            id: 1,
            max: 10,
            min: 10,
            description: "",
            title: "Upcoming Games",
          },
        ],
        successMessage:
          "You're good to go! Click Continue to view seating options.",
        updatedAccountGroup: 0,
        customProgressBarLanguage,
      },
      {
        id: 2,
        amount: 10,
        title: "Dugout Premium 10 Game Pack",
        defaultSelected: true,
        offerMoreMessage: "",
        description:
          "Pick 10 games + save 27% while enjoying seating directly behind the dugout!",
        scrollToTopWhenSelecting: true,
        buckets: [
          {
            id: 1,
            max: 10,
            min: 10,
            description: "",
            title: "Upcoming Games",
          },
        ],
        successMessage:
          "You're good to go! Click Continue to view seating options.",
        updatedAccountGroup: 1,
        customProgressBarLanguage,
      },
      {
        id: 3,
        amount: 10,
        title: "1B/3B Reserved 10 Game Pack",
        offerMoreMessage: "",
        description:
          "Pick 10 games + save 21% and choose seating down the first or third base foul lines!",
        scrollToTopWhenSelecting: true,
        buckets: [
          {
            id: 1,
            max: 10,
            min: 10,
            description: "",
            title: "Upcoming Games",
          },
        ],
        successMessage:
          "You're good to go! Click Continue to view seating options.",
        updatedAccountGroup: 2,
        customProgressBarLanguage,
      },
    ],
  },

  // seat map configs
  seatMapConfigs: {
    mapId: "",
    venueId: "",
    isView3dButtonHidden: true,
    staticSeatMapImage: StaticSeatMap,
    isShowAvailableSeatsButtonHidden: true,
    customSeatMapStaticImageStyle: {
      display: "block",
      marginX: "auto",
      width: "70%",
    },
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: inventoryTypeUIDs.seatLevel,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 3,
    maxGameSelectionCount: 5,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,
    defaultSeatCount: 2,
    checkoutTimerEnabled: false,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    AnalyticsHeader,
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: true,

  // urls
  accountManagerUrl:
    "https://www.milb.com/northwest-arkansas/tickets/my-naturals-tickets",

  // vendor
  platform: vendorUUIDs.tickesdotcom,

  // okta config
  oktaIssuer: "https://ids.mlb.com/oauth2/aus1m088yK07noBfh356",
  oktaClientId: "0oasmcdgh92Jom7TI357",

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    faviconImage: LogoImage,
    seatMap3dPreviewImage: "",
    authBackgroundImage: BackgroundImage,
    findGamesBannerImage: FindGamesBanner,
    checkoutStatusPageImage: BackgroundImage,
    logoImage: LogoImage + "?width=128&height=66",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      hideOverlayLogo: true,
      showLogoWithTitle: false,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at{" "}
        <Link
          href="tel:4799274900"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (479) 927-4900
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#00539f",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,

    purchaseSeats: {
      similarSeatModalBody: (seatTitle: string) => (
        <>
          We are going to apply the same or similar seats to all games in your
          pack. Seat locations will be visible on the checkout page.
        </>
      ),
    },

    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription: (
      <>
        Your Northwest Arkansas Naturals tickets are confirmed! Your tickets
        will be available within 24 hours in your{" "}
        <Link
          target="_blank"
          href="https://www.milb.com/northwest-arkansas/tickets/my-naturals-tickets"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          Account Manager
        </Link>{" "}
        account.
      </>
    ),

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
